import React, { useRef, useState, useEffect } from "react";
import { Document, Page } from "react-pdf";
import { Modal, Input, Button, Select, Tag, Typography, theme } from "antd";
import { LiveAudioVisualizer } from "react-audio-visualize";
import UserBubble from "./UserBubble";

const { Text } = Typography;

// Utility: SVG path for freehand
function pointsToSvgPath(points, width, height) {
  if (!points.length) return "";
  return "M " + points.map(([x, y]) => `${x * width},${y * height}`).join(" L ");
}

export default function PDFCanvasOverlay({
  pdfId,
  page,
  annotations,
  onAddAnnotation,
  drawMode,
  color = null,
  pageWidth = 1,
  pageHeight = 1
}) {
  const overlayRef = useRef();
  const [selecting, setSelecting] = useState(false);
  const [startRect, setStartRect] = useState(null);
  const [liveRect, setLiveRect] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [drawPoints, setDrawPoints] = useState([]);
  const [drawStrokes, setDrawStrokes] = useState([]);
  const [showVoiceModal, setShowVoiceModal] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioStream, setAudioStream] = useState(null);

  const {
    token: {
      colorPrimary,
      colorBgElevated,
      colorBorderSecondary,
      colorText,
      colorBgMask,
      borderRadiusLG,
      colorError,
      colorWarning
    },
  } = theme.useToken();

  // Mock user list (replace with actual user data source in production)
  const users = [
    { value: "user1", label: "User 1" },
    { value: "user2", label: "User 2" },
    { value: "user3", label: "User 3" },
    { value: "user4", label: "User 4" },
  ];

  // Initialize MediaRecorder and AudioContext for voice recording and visualization
  useEffect(() => {
    let stream = null;
    let audioContext = null;
    const setupMediaRecorder = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        setAudioStream(stream);
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContext.createMediaStreamSource(stream);
        const recorder = new MediaRecorder(stream);
        const chunks = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
          }
        };

        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: "audio/webm" });
          setAudioBlob(blob);
          setAudioUrl(URL.createObjectURL(blob));
        };

        setMediaRecorder(recorder);
      } catch (err) {
        console.error("Error accessing microphone:", err);
      }
    };

    if (showVoiceModal) {
      setupMediaRecorder();
    }

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (audioContext) {
        audioContext.close();
      }
    };
  }, [showVoiceModal]);

  // Save freehand strokes as annotations when done
  useEffect(() => {
    if (drawMode !== "draw" && drawStrokes.length > 0) {
      drawStrokes.forEach(stroke => {
        onAddAnnotation({
          pdfId,
          page,
          type: "scribble",
          boundingBox: null,
          commentText: "Freehand Drawing",
          color: stroke.color || colorPrimary,
          scribbleData: JSON.stringify(stroke.points),
        });
      });
      setDrawStrokes([]);
    }
  }, [drawMode, pdfId, page, onAddAnnotation, colorPrimary]);

  // Mouse helpers
  function getOverlaySize() {
    return { width: pageWidth, height: pageHeight };
  }

  // Freehand drawing
  const handleDrawDown = (e) => {
    if (drawMode !== "draw") return;
    setDrawing(true);
    const rect = overlayRef.current.getBoundingClientRect();
    const { width, height } = getOverlaySize();
    const x = (e.clientX - rect.left) / width;
    const y = (e.clientY - rect.top) / height;
    setDrawPoints([[x, y]]);
  };

  const handleDrawMove = (e) => {
    if (!drawing || drawMode !== "draw") return;
    const rect = overlayRef.current.getBoundingClientRect();
    const { width, height } = getOverlaySize();
    const x = (e.clientX - rect.left) / width;
    const y = (e.clientY - rect.top) / height;
    setDrawPoints(prev => [...prev, [x, y]]);
  };

  const handleDrawUp = () => {
    if (!drawing || drawMode !== "draw") return;
    if (drawPoints.length > 1) {
      setDrawStrokes(prev => [...prev, { points: drawPoints, color: color || colorPrimary }]);
    }
    setDrawing(false);
    setDrawPoints([]);
  };

  // Area select handlers (select, voice)
  const handleRectDown = (e) => {
    if (drawMode === "draw") return handleDrawDown(e);
    setSelecting(true);
    const rect = overlayRef.current.getBoundingClientRect();
    const { width, height } = getOverlaySize();
    const x = (e.clientX - rect.left) / width;
    const y = (e.clientY - rect.top) / height;
    setStartRect({ x, y });
    setLiveRect({ x, y, width: 0, height: 0 });
  };

  const handleRectMove = (e) => {
    if (drawMode === "draw") return handleDrawMove(e);
    if (!selecting || !startRect) return;
    const rect = overlayRef.current.getBoundingClientRect();
    const { width, height } = getOverlaySize();
    const x2 = (e.clientX - rect.left) / width;
    const y2 = (e.clientY - rect.top) / height;
    setLiveRect({
      x: Math.min(startRect.x, x2),
      y: Math.min(startRect.y, y2),
      width: Math.abs(x2 - startRect.x),
      height: Math.abs(y2 - startRect.y),
    });
  };

  const handleRectUp = () => {
    if (drawMode === "draw") return handleDrawUp();
    setSelecting(false);
    setStartRect(null);

    if (!liveRect || liveRect.width < 0.01 || liveRect.height < 0.01) {
      setLiveRect(null);
      return;
    }

    if (drawMode === "voice") {
      setShowVoiceModal({
        boundingBox: { ...liveRect },
        page,
        color: color || colorPrimary,
      });
    } else if (drawMode === "select") {
      setShowCommentModal({
        boundingBox: { ...liveRect },
        page,
        color: color || colorPrimary,
      });
    }
    setLiveRect(null);
  };

  // Denormalize bounding box to overlay size (for rendering)
  const absRect = (normRect) => {
    const { width, height } = getOverlaySize();
    return {
      left: normRect.x * width,
      top: normRect.y * height,
      width: normRect.width * width,
      height: normRect.height * height,
    };
  };

  const scribbles = annotations.filter(a => a.type === "scribble" && a.page === page);
  const voiceAnnotations = annotations.filter(a => a.type === "voice" && a.page === page);

  return (
    <>
      <div
        ref={overlayRef}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: pageWidth,
          height: pageHeight,
          pointerEvents: "none",
          zIndex: 10,
        }}
      >
        <UserBubble x={150} y={300} color={colorPrimary} />
        <UserBubble x={350} y={100} color={colorWarning} />


        {/* SVG for freehand drawings */}
        <svg
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 15,
          }}
          width={pageWidth}
          height={pageHeight}
        >
          {/* Saved freehand drawings */}
          {scribbles.map((scrib, idx) => {
            let pts = [];
            try {
              pts = JSON.parse(scrib.scribbleData);
            } catch {}
            return (
              <path
                key={idx}
                d={pointsToSvgPath(pts, pageWidth, pageHeight)}
                stroke={scrib.color || colorPrimary}
                strokeWidth={3}
                fill="none"
                opacity={0.8}
              />
            );
          })}
          {/* Not-yet-saved freehand strokes */}
          {drawMode === "draw" &&
            drawStrokes.map((stroke, idx) => (
              <path
                key={"sess-" + idx}
                d={pointsToSvgPath(stroke.points, pageWidth, pageHeight)}
                stroke={stroke.color || colorPrimary}
                strokeWidth={3}
                fill="none"
                opacity={0.8}
              />
            ))}
          {/* Live line while drawing */}
          {drawMode === "draw" && drawing && drawPoints.length > 1 && (
            <path
              d={pointsToSvgPath(drawPoints, pageWidth, pageHeight)}
              stroke={color || colorPrimary}
              strokeWidth={3}
              fill="none"
              opacity={0.8}
            />
          )}
        </svg>

        {/* Render all saved area comments/voice */}
        {annotations
          .filter(
            (a) =>
              (a.type === "comment" || a.type === "voice") &&
              a.page === page &&
              a.boundingBox
          )
          .map((ann, i) => {
            let bbox = ann.boundingBox;
            if (typeof bbox === "string") {
              try {
                bbox = JSON.parse(bbox);
              } catch {
                bbox = null;
              }
            }
            if (!bbox) return null;
            const { left, top, width, height } = absRect(bbox);
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left,
                  top,
                  width,
                  height,
                  background: ann.color || colorBgElevated,
                  opacity: 0.5,
                  border: `2px solid ${ann.color || colorPrimary}`,
                  borderRadius: borderRadiusLG,
                  pointerEvents: "auto",
                  color: colorText,
                  fontSize: 14,
                  padding: 4,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  zIndex: 30
                }}
                title={ann.commentText || ann.transcript || ""}
              >
                {ann.type === "comment"
                  ? ann.commentText
                  : ann.type === "voice"
                  ? "🎤"
                  : ""}
              </div>
            );
          })}

        {/* Live rectangle while dragging */}
        {(drawMode === "select" || drawMode === "voice") &&
          selecting &&
          liveRect && (() => {
            const { left, top, width, height } = absRect(liveRect);
            return (
              <div
                style={{
                  position: "absolute",
                  left,
                  top,
                  width,
                  height,
                  background: color || colorBgElevated,
                  opacity: 0.3,
                  border: `2px dashed ${color || colorPrimary}`,
                  borderRadius: borderRadiusLG,
                  pointerEvents: "none",
                  zIndex: 99,
                }}
              />
            );
          })()}

        {/* Mouse capture layer */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: pageWidth,
            height: pageHeight,
            pointerEvents: "auto",
            cursor:
              drawMode === "draw"
                ? "crosshair"
                : drawMode === "select" || drawMode === "voice"
                ? "pointer"
                : "default",
            zIndex: 200,
          }}
          onMouseDown={drawMode === "draw" ? handleDrawDown : handleRectDown}
          onMouseMove={drawMode === "draw" ? handleDrawMove : handleRectMove}
          onMouseUp={drawMode === "draw" ? handleDrawUp : handleRectUp}
        />
      </div>

      {/* Voice Recorder Modal */}
      <Modal
        title="Record Voice Annotation"
        open={!!showVoiceModal}
        onCancel={() => {
          setShowVoiceModal(null);
          setAudioBlob(null);
          setAudioUrl(null);
          setIsRecording(false);
          setCommentText("");
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowVoiceModal(null);
              setAudioBlob(null);
              setAudioUrl(null);
              setIsRecording(false);
              setCommentText("");
            }}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            disabled={!audioBlob}
            onClick={() => {
              if (audioBlob) {
                onAddAnnotation({
                  pdfId,
                  page: showVoiceModal?.page,
                  type: "voice",
                  boundingBox: showVoiceModal?.boundingBox,
                  voiceBlob: audioBlob,
                  transcript: commentText,
                  color: showVoiceModal?.color || colorPrimary,
                });
              }
              setShowVoiceModal(null);
              setAudioBlob(null);
              setAudioUrl(null);
              setIsRecording(false);
              setCommentText("");
            }}
          >
            Save
          </Button>,
        ]}
        maskStyle={{ backgroundColor: colorBgMask }}
      >
          <div style={{ display: "flex", gap: 8, marginBottom: 16, marginTop:16 }}>
            <Button
              onClick={() => {
                if (mediaRecorder && !isRecording) {
                  mediaRecorder.start();
                  setIsRecording(true);
                }
              }}
              disabled={isRecording}
              type="primary"
            >
              Start Recording
            </Button>
            <Button
              onClick={() => {
                if (mediaRecorder && isRecording) {
                  mediaRecorder.stop();
                  setIsRecording(false);
                }
              }}
              disabled={!isRecording}
            >
              Stop Recording
            </Button>
          </div>
          {audioStream && (
            <LiveAudioVisualizer
              mediaRecorder={mediaRecorder}
              stream={audioStream}
              width={400}
              height={100}
              barWidth={3}
              barColor={colorPrimary}
              style={{ marginBottom: 16 }}
            />
          )}
          {!isRecording && audioUrl && (
            <>
              <Text style={{ display: "block", marginBottom: 8 }}>Playback</Text>
              <audio
                src={audioUrl}
                controls
                style={{ width: "100%", marginBottom: 16 }}
              />
            </>
          )}
          {!isRecording && (
            <>
              <Text style={{ display: "block", marginBottom: 8 }}>Transcript (Optional)</Text>
              <Input.TextArea
                placeholder="Enter transcript"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                rows={4}
                style={{ marginBottom: 16 }}
              />
              <Text style={{ display: "block", marginBottom: 8 }}>Saved Voice Annotations</Text>
              {voiceAnnotations.length === 0 ? (
                <span style={{ color: colorError }}>No voice annotations saved</span>
              ) : (
                voiceAnnotations.map((ann, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                      padding: 8,
                      border: `1px solid ${colorBorderSecondary}`,
                      borderRadius: borderRadiusLG,
                    }}
                  >
                    <audio
                      src={URL.createObjectURL(ann.voiceBlob)}
                      controls
                      style={{ flex: 1 }}
                    />
                    <Text>{ann.transcript || "No transcript provided"}</Text>
                  </div>
                ))
              )}
            </>
          )}
      </Modal>

      {/* Comment Input Modal */}
      <Modal
        title="Add Your Comment"
        open={!!showCommentModal}
        onCancel={() => {
          setShowCommentModal(null);
          setCommentText("");
          setVisibility("private");
          setSelectedUsers([]);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowCommentModal(null);
              setCommentText("");
              setVisibility("private");
              setSelectedUsers([]);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            disabled={!commentText}
            onClick={() => {
              if (commentText) {
                onAddAnnotation({
                  pdfId,
                  page: showCommentModal?.page,
                  type: "comment",
                  boundingBox: showCommentModal?.boundingBox,
                  commentText,
                  visibility,
                  sharedWith: visibility === "public" ? selectedUsers : [],
                  color: showCommentModal?.color || colorPrimary,
                });
              }
              setShowCommentModal(null);
              setCommentText("");
              setVisibility("private");
              setSelectedUsers([]);
            }}
          >
            Save
          </Button>,
        ]}
        maskStyle={{ backgroundColor: colorBgMask }}
      >
          <Input.TextArea
            placeholder="Enter your comment"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            rows={4}
            style={{ marginBottom: 16 }}
          />
          <Text style={{ display: "block", marginBottom: 8 }}>Visibility</Text>
          <Select
            value={visibility}
            onChange={setVisibility}
            style={{ width: "100%", marginBottom: 16 }}
            options={[
              { value: "private", label: "Private" },
              { value: "public", label: "Public" },
            ]}
          />
          {visibility === "public" && (
            <>
              <Text style={{ display: "block", marginBottom: 8 }}>Share with Users</Text>
              <Select
                placeholder="Select users to share with"
                style={{ width: "100%", marginBottom: 16 }}
                onChange={(value) => {
                  if (!selectedUsers.includes(value)) {
                    setSelectedUsers([...selectedUsers, value]);
                  }
                }}
                options={users.filter(user => !selectedUsers.includes(user.value))}
                value={null}
              />
              <Text style={{ display: "block", marginBottom: 8 }}>Selected Users</Text>
              <div style={{ marginBottom: 16 }}>
                {selectedUsers.length === 0 ? (
                  <span style={{ color: colorError }}>No users selected</span>
                ) : (
                  selectedUsers.map(userId => (
                    <Tag
                      key={userId}
                      closable
                      onClose={() => setSelectedUsers(selectedUsers.filter(id => id !== userId))}
                      style={{ margin: "4px" }}
                    >
                      {users.find(user => user.value === userId)?.label || userId}
                    </Tag>
                  ))
                )}
              </div>
            </>
          )}
      </Modal>
    </>
  );
}